import { css } from '@emotion/react'
import Link from 'next/link'
import { colors, font, mq } from 'plume-ui'
import React from 'react'
import { articlePath, ARTICLE_HREF } from '@/lib/url'
import { ArticleCommon } from '../../../types'
import { TagButton, TagButtonGroup } from '../../atoms/TagButton'
import { PublishedDate } from '../../common/PublishedDate'
import { LockIcon } from './LockIcon'

type Props = {
  article: ArticleCommon
  className?: string
}

export const ArticleCard: React.FC<Props> = (props) => {
  return (
    <li
      css={css`
        padding: 0 8px;
        transition: 0.3s;
        background: ${colors.white00};
        ${mq.large} {
          padding: 0 0 16px;
          border-radius: 8px;
          overflow: hidden;
          &:hover {
            box-shadow: 0 0 15px 0 rgba(22, 22, 22, 0.1);
          }
        }
      `}
      className={props.className}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          grid-row-gap: 8px;
          padding: 16px 16px;
          :not(li:last-child &) {
            border-bottom: 1px solid ${colors.border};
          }
          ${mq.large} {
            flex-direction: column;
            padding: 0;
            :not(li:last-child &) {
              border-bottom: none;
            }
          }
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: row-reverse;
            justify-content: space-between;
            grid-column-gap: 8px;
            ${mq.large} {
              flex-direction: column;
            }
          `}
        >
          <Link href={ARTICLE_HREF} as={articlePath(props.article)} passHref>
            <a
              css={css`
                position: relative;
                flex-shrink: 0;
                flex-basis: 112px;
                ::after {
                  content: '';
                  position: absolute;
                  left: 0;
                  top: 0;
                  display: block;
                  width: 100%;
                  height: 100%;
                  background-color: #000;
                  opacity: 0;
                  transition: opacity 0.2s ease;
                }
                :hover {
                  ::after {
                    opacity: 0.08;
                  }
                }
              `}
            >
              <img
                src={props.article.headerImage.url}
                alt={props.article.title}
                css={css`
                  display: block;
                  border-radius: 2px;
                `}
              />
            </a>
          </Link>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              ${mq.large} {
                font-size: 15px;
              }
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                ${mq.large} {
                  flex-direction: column-reverse;
                  padding: 16px 24px 0;
                }
              `}
            >
              <p
                css={css`
                  margin: 0;
                `}
              >
                <Link
                  href={ARTICLE_HREF}
                  as={articlePath(props.article)}
                  passHref
                >
                  <a className="article-title">
                    <LockIcon article={props.article} />
                    <span
                      css={css`
                        color: ${colors.textStrong};
                        font-weight: ${font.weight.bold};
                        font-size: 15px;
                        line-height: 1.5;
                        letter-spacing: 0.02em;
                        transition: text-decoration 0.2s linear;
                        text-decoration: underline solid transparent;
                        &:hover {
                          text-decoration: underline solid ${colors.textStrong};
                        }
                        ${mq.large} {
                          font-size: 16px;
                        }
                      `}
                    >
                      {props.article.title}
                    </span>
                  </a>
                </Link>
              </p>
              <p
                css={css`
                  margin: 0;
                `}
              >
                <PublishedDate
                  css={css`
                    font-size: 12px;
                    line-height: 1.5;
                    letter-spacing: 0.02em;
                    ${mq.large} {
                      font-size: 14px;
                    }
                  `}
                >
                  {props.article.publishedAt}
                </PublishedDate>
              </p>
            </div>
          </div>
        </div>
        {props.article.newTags && (
          <TagButtonGroup
            css={css`
              ${mq.large} {
                padding: 0 24px;
              }
            `}
          >
            {props.article.newTags.map((tag, i) => (
              <TagButton tag={tag} key={i} />
            ))}
          </TagButtonGroup>
        )}
      </div>
    </li>
  )
}
